import React from 'react';

import type {AutoDeliveryDcxStatusBadgeStatus} from '@/views/AutoDeliveryDcx/AutoDeliveryDcxStatusBadge';

import useAutoDeliveryDcxDetails from '../hooks/useAutoDeliveryDcxDetails';
import AutoDeliveryDcxHelpSupport from './AutoDeliveryDcxHelpSupport';
import AutoDeliveryDcxMobileNumber from './AutoDeliveryDcxMobileNumber';
import AutoDeliveryDcxNextDeliveryDetails from './AutoDeliveryDcxNextDeliveryDetails';
import AutoDeliveryDcxOrder from './AutoDeliveryDcxOrder';
import AutoDeliveryDcxOrderAddress from './AutoDeliveryDcxOrderAddress';
import AutoDeliveryDcxOrderDetails from './AutoDeliveryDcxOrderDetails';
import AutoDeliveryDcxPageDesktopLayout from './AutoDeliveryDcxPageDesktopLayout';
import AutoDeliveryDcxPastOrdersButton from './AutoDeliveryDcxPastOrdersButton';
import AutoDeliveryDcxPaymentMethod from './AutoDeliveryDcxPaymentMethod';

interface AutoDeliveryDcxDetailsDesktopProps {
    status: AutoDeliveryDcxStatusBadgeStatus;
    extraSection?: React.ReactNode;
    showNextDeliveryHero?: boolean;
    footerActions?: React.ReactNode;
}

const AutoDeliveryDcxDetailsDesktop = ({
    status,
    extraSection,
    showNextDeliveryHero,
    footerActions,
}: AutoDeliveryDcxDetailsDesktopProps) => {
    const {
        autoDelivery: {
            minDeliveryDaysCount,
            maxDeliveryDaysCount,
            frequency,
            isFreezed,
            status: autoDeliveryStatus,
            statusUpdateReason,
            nextDeliveryDate,
            maxNextDeliveryDate,
        },
        order: {orderId, deliveryAddress, paymentInfo, products, price, status: orderStatus},
        isOrderStatusExpected,
    } = useAutoDeliveryDcxDetails();

    return (
        <AutoDeliveryDcxPageDesktopLayout>
            <AutoDeliveryDcxPageDesktopLayout.Start action={<AutoDeliveryDcxPastOrdersButton />}>
                {isOrderStatusExpected || !showNextDeliveryHero ? (
                    <AutoDeliveryDcxOrderDetails
                        status={status}
                        frequency={frequency}
                        minDeliveryDaysCount={minDeliveryDaysCount}
                        maxDeliveryDaysCount={maxDeliveryDaysCount}
                        extraSection={extraSection}
                    />
                ) : (
                    <AutoDeliveryDcxNextDeliveryDetails
                        status={autoDeliveryStatus}
                        orderStatus={orderStatus}
                        statusUpdateReason={statusUpdateReason}
                        minDeliveryDaysCount={minDeliveryDaysCount}
                        maxDeliveryDaysCount={maxDeliveryDaysCount}
                        nextDeliveryDate={nextDeliveryDate}
                        maxNextDeliveryDate={maxNextDeliveryDate}
                        extraSection={extraSection}
                    />
                )}
                <AutoDeliveryDcxOrder
                    className='mt-0 snap-start'
                    products={products}
                    price={price}
                    isEditAllowed
                    isUpdateDisabled={isFreezed}
                    isCompact
                />
            </AutoDeliveryDcxPageDesktopLayout.Start>
            <AutoDeliveryDcxPageDesktopLayout.End
                footer={footerActions && <div className='flex flex-col gap-3'>{footerActions}</div>}
            >
                {showNextDeliveryHero && !isOrderStatusExpected && (
                    <AutoDeliveryDcxOrderDetails
                        headingAs='h2'
                        status={status}
                        orderId={orderId}
                        frequency={frequency}
                        minDeliveryDaysCount={minDeliveryDaysCount}
                        maxDeliveryDaysCount={maxDeliveryDaysCount}
                    />
                )}
                <AutoDeliveryDcxPaymentMethod isUpdateDisabled={isFreezed} paymentInfo={paymentInfo} />
                <AutoDeliveryDcxOrderAddress address={deliveryAddress} isEditAllowed isUpdateDisabled={isFreezed} />
                <AutoDeliveryDcxMobileNumber />
                <AutoDeliveryDcxHelpSupport />
            </AutoDeliveryDcxPageDesktopLayout.End>
        </AutoDeliveryDcxPageDesktopLayout>
    );
};

export default AutoDeliveryDcxDetailsDesktop;
